import { useDepartmentText } from 'model/DepartmentTerminology';
import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/department/columns/ActionsColumn';
import AddressColumn from 'components/tables/data/department/columns/AddressColumn';
import ContactPersonColumn from 'components/tables/data/department/columns/ContactPersonColumn';
import DepartmentIdColumn from 'components/tables/data/department/columns/DepartmentIdColumn';
import OrganizationNameColumn from 'components/tables/data/department/columns/OrganizationNameColumn';
import SupportsKepColumn from 'components/tables/data/department/columns/SupportsKepColumn';
import { DepartmentRow } from 'components/tables/data/department/types';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import SupportsKepSelectFilter from 'components/tables/filters/SupportsKepSelectFilter';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { ADD_DEPARTMENT_PAGE_PATH } from 'routes/paths';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import DepartmentNameColumn from 'components/tables/data/department/columns/DepartmentNameColumn';
import useDepartmentLevelColumn, {
  DEPARTMENT_LEVEL_COLUMN_ID,
} from 'components/tables/data/department/DepartmentLevelColumn';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import { Popover, Select } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import ActionIcon from 'components/ActionIcon';
import useLocalStorage from 'hooks/use-local-storage';
import AddDepartmentPage from 'pages/admin-module/department/AddDepartmentPage';
import EditDepartmentPage from 'pages/admin-module/department/EditDepartmentPage';
import FormPresentationShell from 'components/forms/FormPresentationShell';
import {
  DEPARTMENT_FORM_MODE_OPTIONS,
  DepartmentFormMode,
  isDepartmentFormMode,
} from 'components/tables/data/department/form-presentation';

interface DepartmentOverlay {
  kind: 'create' | 'edit';
  departmentId?: number;
  departmentName?: string;
  organizationId?: number;
  parentDepartmentId?: number;
  key: number;
}

interface DepartmentTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
}

/**
 * Table which displays departments.
 */
const DepartmentTable = forwardRef<IDataTablePublic, DepartmentTableProps>(
  ({ initialFilters, suppressSaveProfiles, suppressProfileChangedNotification }, ref) => {
    const departmentText = useDepartmentText();
    const { mode } = useDepartmentUiMode();
    const { getAction } = useApi();
    const { tableActionMode } = useResponsiveDesign();
    const hierarchy = useDepartmentLevelColumn('manage-departments');
    const [savedPresentation, , updatePresentation] = useLocalStorage('objekto.departments.form-presentation', {
      mode: 'same-tab' as DepartmentFormMode,
    });
    const presentationMode = isDepartmentFormMode(savedPresentation.mode) ? savedPresentation.mode : 'same-tab';
    const overlayMode = presentationMode === 'drawer' || presentationMode === 'modal';
    const [overlay, setOverlay] = useState<DepartmentOverlay | null>(null);
    const overlaySequence = useRef(0);
    const [overlayDirty, setOverlayDirty] = useState(false);
    const [overlayBusy, setOverlayBusy] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);

    const closeOverlay = useCallback(() => {
      setOverlay(null);
      setOverlayDirty(false);
      setOverlayBusy(false);
    }, []);

    const openOverlay = useCallback((next: Omit<DepartmentOverlay, 'key'>) => {
      overlaySequence.current += 1;
      setOverlay({ ...next, key: overlaySequence.current });
      setOverlayDirty(false);
      setOverlayBusy(false);
    }, []);

    const refreshDepartments = useCallback(() => setRefreshToken((token) => token + 1), []);

    const action = useCallback(async () => {
      const action = getAction('DepartmentList');
      const departments = await action({ query: { filters: { 'permissionSlug.eq': 'manage-departments' } } });
      return departments;
    }, [getAction]);

    const columns = useMemo<ColDef[]>(
      () => [
        {
          field: 'departmentId',
          headerName: 'ID',
          sortable: true,
          unSortIcon: true,
          width: 76,
          minWidth: 76,
          maxWidth: 76,
          resizable: false,
          cellRenderer: DepartmentIdColumn,
        },
        {
          field: 'organization.organizationName',
          headerName: 'Organizácia',
          minWidth: 200,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: OrganizationMultiSelectFilter.configure({ permissionSlug: 'manage-departments' }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: OrganizationNameColumn,
        },
        ...(mode === 'tree' ? [hierarchy.column] : []),
        {
          field: 'departmentName',
          headerName: 'Názov',
          minWidth: 400,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DepartmentNameColumn,
        },
        {
          field: 'departmentNumber',
          headerName: departmentText('Číslo položky úrovne', 'Číslo strediska'),
          hide: true,
        },
        {
          field: 'organization.organizationId',
          headerName: 'ID organizácie',
          hide: true,
          filter: OrganizationMultiColumnFilter,
        },
        { field: 'streetAddress', headerName: 'Ulica', hide: true },
        { field: 'city', headerName: 'Mesto', hide: true },
        { field: 'zip', headerName: 'PSČ', hide: true },
        { field: 'country', headerName: 'Krajina', hide: true },
        {
          field: 'address',
          headerName: 'Adresa',
          minWidth: 200,
          cellRenderer: AddressColumn,
        },
        { field: 'contactPerson.userId', headerName: 'ID kontaktnej osoby', hide: true },
        { field: 'contactPerson.fullName', headerName: 'Meno kontaktnej osoby', hide: true },
        {
          field: 'contactPerson.firstName',
          headerName: '',
          filter: true,
          hide: true,
        },
        {
          field: 'contactPerson.lastName',
          headerName: '',
          filter: true,
          hide: true,
        },
        {
          field: 'contactPerson.email',
          headerName: '',
          filter: true,
          hide: true,
        },
        {
          field: 'contactPerson',
          headerName: 'Kontaktná osoba',
          minWidth: 200,
          cellRenderer: ContactPersonColumn,
        },
        {
          valueGetter: ({ data: { status } }: { data: DepartmentRow }) => (status ? '1' : '0'),
          field: 'status',
          headerName: 'Aktívne',
          filter: true,
          hide: true,
        },
        {
          valueGetter: ({ data: { supportsKep } }: { data: DepartmentRow }) => (supportsKep ? '1' : '0'),
          field: 'supportsKep',
          headerName: 'Podporuje KEP',
          minWidth: 180,
          sortable: true,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: SupportsKepSelectFilter,
          unSortIcon: true,
          cellRenderer: SupportsKepColumn,
        },
        ...(mode === 'tree'
          ? [
              {
                field: 'parentDepartmentId',
                headerName: departmentText('ID nadradenej položky úrovne', 'ID nadradeného strediska'),
                hide: true,
              },
            ]
          : []),
        {
          field: '_actions',
          headerName: '',
          pinned: 'right',
          width: getResponsiveActionsColumnWidth(194, tableActionMode),
          minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
          maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
          cellRenderer: ActionsColumn,
          cellRendererParams: ({ data }: { data: DepartmentRow }) => ({
            isLeafLevel: !hierarchy.nonLeafLevelSlugs.has(
              data.breadcrumbs.find(({ departmentId }) => departmentId === data.departmentId)?.hierarchyLevelSlug ?? ''
            ),
            presentationMode,
            onOpenEdit: (departmentId: number, departmentName: string) =>
              openOverlay({ kind: 'edit', departmentId, departmentName }),
            onOpenChild: (parentDepartmentId: number, organizationId: number) =>
              openOverlay({ kind: 'create', parentDepartmentId, organizationId }),
          }),
        },
      ],
      [
        departmentText,
        hierarchy.column,
        hierarchy.nonLeafLevelSlugs,
        mode,
        tableActionMode,
        presentationMode,
        openOverlay,
      ]
    );

    return (
      <>
        <DataTable
          ref={ref}
          tableId="departments"
          title={departmentText('Položky úrovne', 'Strediská')}
          addButtonText={departmentText('Pridať položku úrovne', 'Pridať stredisko')}
          toggleDiscardedLabel="Zobraziť vyradené"
          columns={columns}
          action={action}
          addButtonTarget={
            overlayMode
              ? undefined
              : `${ADD_DEPARTMENT_PAGE_PATH.original}${presentationMode === 'new-tab' ? '?presentation=new-tab' : ''}`
          }
          addButtonLinkTarget={presentationMode === 'new-tab' ? '_blank' : undefined}
          onAddButtonClick={overlayMode ? () => openOverlay({ kind: 'create' }) : undefined}
          addButtonActions={
            <Popover position="top-end" width={240} withinPortal withArrow>
              <Popover.Target>
                <ActionIcon size="md" variant="primary" aria-label="Nastavenia formulára" title="Nastavenia formulára">
                  <IconSettings size={18} stroke={1.5} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Select
                  label="Spôsob otvorenia formulára"
                  data={DEPARTMENT_FORM_MODE_OPTIONS}
                  value={presentationMode}
                  withinPortal={false}
                  onChange={(value) => {
                    if (isDepartmentFormMode(value)) updatePresentation({ mode: value });
                  }}
                />
              </Popover.Dropdown>
            </Popover>
          }
          dataRefreshToken={refreshToken}
          silentDataRefresh
          initialFilters={initialFilters}
          suppressSaveProfiles={suppressSaveProfiles}
          suppressProfileChangedNotification={suppressProfileChangedNotification}
          profileColumnAliases={hierarchy.profileColumnAliases}
          profileMigrationReady={hierarchy.profileMigrationReady}
          resolveProfileFilterColumnId={hierarchy.resolveProfileFilterColumnId}
          dataExport={{
            modalTitle: departmentText('Exportovať položky úrovne', 'Exportovať strediská'),
            fileName: departmentText('polozky-urovne.xlsx', 'strediska.xlsx'),
            columnReplacements: {
              address: ['streetAddress', 'city'],
              contactPerson: ['contactPerson.userId', 'contactPerson.fullName'],
            },
            columnKeys: [
              'departmentId',
              ...(mode === 'tree' ? [DEPARTMENT_LEVEL_COLUMN_ID] : []),
              'departmentName',
              ...(mode === 'tree' ? ['parentDepartmentId'] : []),
              'departmentNumber',
              'streetAddress',
              'city',
              'zip',
              'country',
              'status',
              'supportsKep',
              'contactPerson.userId',
              'contactPerson.fullName',
              'organization.organizationId',
              'organization.organizationName',
            ],
          }}
        />
        {overlay && overlayMode && (
          <FormPresentationShell
            key={overlay.key}
            mode={presentationMode === 'drawer' ? 'drawer' : 'modal'}
            title={
              overlay.kind === 'create'
                ? departmentText('Pridanie novej položky úrovne', 'Pridanie nového strediska')
                : departmentText(
                    `Úprava položky úrovne: ${overlay.departmentName ?? ''}`,
                    `Úprava strediska: ${overlay.departmentName ?? ''}`
                  )
            }
            dirty={overlayDirty}
            busy={overlayBusy}
            onClose={closeOverlay}
          >
            {(requestClose) =>
              overlay.kind === 'create' ? (
                <AddDepartmentPage
                  embedded
                  initialPlacement={{
                    organizationId: overlay.organizationId ? String(overlay.organizationId) : '',
                    parentDepartmentId: overlay.parentDepartmentId ? String(overlay.parentDepartmentId) : '',
                  }}
                  onCreated={refreshDepartments}
                  onComplete={closeOverlay}
                  onClose={requestClose}
                  onDirtyChange={setOverlayDirty}
                  onBusyChange={setOverlayBusy}
                />
              ) : (
                <EditDepartmentPage
                  embedded
                  departmentIdOverride={String(overlay.departmentId)}
                  onSaved={() => {
                    refreshDepartments();
                    closeOverlay();
                  }}
                  onClose={requestClose}
                  onDirtyChange={setOverlayDirty}
                  onBusyChange={setOverlayBusy}
                />
              )
            }
          </FormPresentationShell>
        )}
      </>
    );
  }
);

export default DepartmentTable;
